import { useEffect, useState } from 'react'

import type { CustomerUserRoleItem } from '../../../services/usersManagement'
import type { MultiSelectOption } from '../../copSubscription/components/MultiSelectList'
import MultiSelectList from '../../copSubscription/components/MultiSelectList'
import type { SelectOption } from '../../copSubscription/components/SearchableSelect'
import SearchableSelect from '../../copSubscription/components/SearchableSelect'
import { validateAssignment } from '../hooks/useAssignmentValidation'
import type { FormUpdate, UserFormData } from '../hooks/useUserManagementForm'
import type { UserManagementConfig } from '../UserManagement.config'
import { USER_MANAGEMENT_DEFAULTS as D } from '../UserManagement.constants'
import {
    type Assignment,
    findLabel,
    getCustomerLabel,
    getSubmitLabel,
    isNoSlaCustomer,
} from './AssignUserTab.utils'
export type AssignUserTabProps = {
    onCancel: () => void
    onSubmit: (roles: CustomerUserRoleItem[]) => void
    submitting: boolean
    isEditMode?: boolean
    initialRoles?: CustomerUserRoleItem[]
    assignments: Assignment[]
    onAssignmentsChange: (next: Assignment[]) => void
    formData: UserFormData
    update: FormUpdate
    customerOptions: SelectOption[]
    roleOptions: SelectOption[]
    productOptions: MultiSelectOption[]
    loading: boolean
    hideProductDropdown: boolean
    disabledRoleValues: Set<string>
    isLoggedInAsUserManager?: boolean
    config?: UserManagementConfig
    searchingCustomers?: boolean
    loadingMoreCustomers?: boolean
    hasMoreCustomers?: boolean
    onCustomerSearch?: (term: string) => void
    onLoadMoreCustomers?: () => void
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const AssignUserTab = ({ onCancel, onSubmit, submitting, isEditMode = false, initialRoles = [], assignments, onAssignmentsChange, formData, update, customerOptions, roleOptions, productOptions, loading, hideProductDropdown, disabledRoleValues, isLoggedInAsUserManager = false, config, searchingCustomers = false, loadingMoreCustomers = false, hasMoreCustomers = false, onCustomerSearch, onLoadMoreCustomers }: AssignUserTabProps) => {
    const [fieldTouched, setFieldTouched] = useState(false)
    const [roleTouched, setRoleTouched] = useState(false)
    const [submitTouched, setSubmitTouched] = useState(false)
    const [assignmentError, setAssignmentError] = useState('')

    useEffect(() => {
        if (!isEditMode || !initialRoles.length || !customerOptions.length || !roleOptions.length) return
        onAssignmentsChange(initialRoles.map(r => ({
            customerId: r.customerId,
            customerLabel: findLabel(customerOptions, r.customerId, r.customerId),
            roleId: String(r.roleId),
            roleLabel: findLabel(roleOptions, String(r.roleId), String(r.roleId)),
            productIds: r.products.map(String),
            productLabels: r.products.map(id => findLabel(productOptions, String(id), String(id))),
        })))
    }, [isEditMode, initialRoles, customerOptions, roleOptions, productOptions, onAssignmentsChange])

    const assignHeading = config?.assignAccounts?.heading ?? D.HEADING_ASSIGN_USER
    const multiAccountLabel = config?.assignAccounts?.multiAccountLabel ?? D.LABEL_MULTI_ACCOUNT
    const accountsLabel = config?.assignAccounts?.accountsLabel ?? D.LABEL_ACCOUNTS
    const accountsPlaceholder = config?.assignAccounts?.accountsPlaceholder ?? D.PLACEHOLDER_ACCOUNTS
    const userRoleLabel = config?.assignAccounts?.userRoleLabel ?? D.LABEL_USER_ROLE
    const userRolePlaceholder = config?.assignAccounts?.userRolePlaceholder ?? D.PLACEHOLDER_USER_ROLE
    const productLabel = config?.assignAccounts?.productLabel ?? D.LABEL_PRODUCT
    const productPlaceholder = config?.assignAccounts?.productPlaceholder ?? D.PLACEHOLDER_PRODUCT
    const cancelLabel = config?.cancelLabel ?? D.BTN_CANCEL
    const submitLabel = config?.submitLabel ?? (isEditMode ? D.BTN_SAVE_CHANGES : D.BTN_ADD_USER)
    const validationRole = config?.assignAccounts?.validation?.role ?? D.VALIDATION_ROLE
    const validationProduct = config?.assignAccounts?.validation?.product ?? D.VALIDATION_PRODUCT
    const validationNoAssignments = config?.assignAccounts?.validation?.noAssignments ?? D.VALIDATION_NO_ASSIGNMENTS

    useEffect(() => {
        if (disabledRoleValues.has(formData.selectedRole)) update('selectedRole', '')
    }, [disabledRoleValues])

    useEffect(() => {
        if (hideProductDropdown && formData.selectedProducts.length > 0) update('selectedProducts', [])
    }, [hideProductDropdown])

    const availableRoleOptions = roleOptions.map(o => ({ ...o, disabled: disabledRoleValues.has(o.value) }))
    const fieldsDisabled = loading || (assignments.length > 0 && !formData.isMultiTenantUser)
    const productDisabled = fieldsDisabled

    const validateAndAddAssignment = (): boolean => {
        const productValid = hideProductDropdown || formData.selectedProducts.length > 0
        if (!formData.selectedCustomer || !formData.selectedRole || !productValid) return false

        const roleLabel = findLabel(roleOptions, formData.selectedRole, formData.selectedRole)
        const error = validateAssignment({
            customerId: formData.selectedCustomer,
            roleLabel,
            hideProductDropdown,
            selectedProducts: formData.selectedProducts,
            existingAssignments: assignments,
            restrictedRoleMessage: validationRole,
        })
        if (error) {
            setAssignmentError(error)
            return false
        }
        return true
    }

    const addToAssignments = () => {
        const customerLabel = findLabel(customerOptions, formData.selectedCustomer, formData.selectedCustomer)
        const productLabels = formData.selectedProducts.map(id => findLabel(productOptions, id, id))
        const roleLabel = findLabel(roleOptions, formData.selectedRole, formData.selectedRole)

        onAssignmentsChange([...assignments, {
            customerId: formData.selectedCustomer,
            customerLabel,
            roleId: formData.selectedRole,
            roleLabel,
            productIds: [...formData.selectedProducts],
            productLabels,
        }])
        // clear fields after adding so user can add another
        update('selectedCustomer', '')
        update('selectedRole', '')
        update('selectedProducts', [])
        setFieldTouched(false)
        setRoleTouched(false)
    }

    const handleAddAssignment = () => {
        setFieldTouched(true)
        setRoleTouched(true)
        setAssignmentError('')
        if (validateAndAddAssignment()) {
            addToAssignments()
        }
    }

    const removeAssignment = (index: number) => {
        onAssignmentsChange(assignments.filter((_, i) => i !== index))
        setAssignmentError('')
    }

    const isAdminLockedMode = formData.userType === 'admin'
    const hasPendingAssignment = Boolean(
        formData.selectedCustomer &&
        formData.selectedRole &&
        (hideProductDropdown || formData.selectedProducts.length > 0),
    )

    const handleSubmit = () => {
        setSubmitTouched(true)
        if (assignments.length === 0 && !isAdminLockedMode) return
        const roles: CustomerUserRoleItem[] = assignments.map(a => ({
            customerId: a.customerId,
            roleId: a.roleId,
            products: a.productIds.map(Number),
        }))
        onSubmit(roles)
    }

    return (
        <div className="um-form-section">
            <div className="um-heading">{assignHeading}</div>

            <div className="um-checkbox-row">
                <input
                    type="checkbox"
                    id="multiAccount"
                    checked={formData.isMultiTenantUser}
                    onChange={e => {
                        const checked = e.target.checked
                        update('isMultiTenantUser', checked)
                        // unticking after multiple accounts were added clears the stale selections below
                        if (!checked && assignments.length > 1) {
                            onAssignmentsChange([])
                        }
                    }}
                    disabled={isLoggedInAsUserManager || isNoSlaCustomer(formData.selectedCustomer)}
                />
                <label htmlFor="multiAccount">{multiAccountLabel}</label>
            </div>

            <div className="form-group">
                <label htmlFor="accounts"><span className="required-mark">*</span>{accountsLabel}</label>
                <SearchableSelect
                    id="accounts"
                    options={customerOptions}
                    value={formData.selectedCustomer}
                    onChange={v => update('selectedCustomer', v)}
                    placeholder={accountsPlaceholder}
                    loading={loading}
                    disabled={fieldsDisabled || formData.userType === 'manager'}
                    invalid={fieldTouched && !formData.selectedCustomer}
                    onSearchChange={onCustomerSearch}
                    onLoadMore={onLoadMoreCustomers}
                    loadingMore={loadingMoreCustomers}
                    hasMore={hasMoreCustomers}
                    searching={searchingCustomers}
                />
            </div>
            <div className="form-group">
                <label htmlFor="userRole">
                    <span className="required-mark">*</span>{userRoleLabel}
                    <button
                        type="button"
                        className="um-role-info-btn"
                        title="Show Explanation"
                        aria-label="Show Explanation"
                        onClick={() => window.dispatchEvent(new CustomEvent('cop:show-role-explanation'))}
                    >
                        &#9432;
                    </button>
                </label>
                <SearchableSelect
                    id="userRole"
                    options={availableRoleOptions}
                    value={formData.selectedRole}
                    onChange={v => {
                        update('selectedRole', v)
                        if (v) setRoleTouched(false)
                    }}
                    placeholder={userRolePlaceholder}
                    loading={loading}
                    disabled={fieldsDisabled}
                    invalid={roleTouched && !formData.selectedRole}
                />
                {roleTouched && !formData.selectedRole && (
                    <p className="field-error"><span className="field-error__icon">i</span>{validationRole}</p>
                )}
            </div>

            {!hideProductDropdown && (
            <div className="form-group">
                <label><span className="required-mark">*</span>{productLabel}</label>
                <div className="um-product-field userManagementMultifield">
                    <MultiSelectList
                        options={productOptions}
                        selectedValues={formData.selectedProducts}
                        onChange={v => update('selectedProducts', v)}
                        placeholder={productPlaceholder}
                        loading={loading}
                        disabled={productDisabled}
                        disabledMessage={D.MSG_SELECT_ACCOUNT_ROLE_FIRST}
                        invalid={fieldTouched && formData.selectedProducts.length === 0}
                    />
                </div>
                {formData.selectedProducts.length > 0 && (
                    <div className="um-add-row">
                        <button
                            type="button"
                            className="btn btn-add"
                            onClick={handleAddAssignment}
                            disabled={fieldsDisabled || !formData.selectedRole}
                            aria-label={D.ARIA_ADD_ASSIGNMENT}
                            title={D.TOOLTIP_ADD_ASSIGNMENT}
                        >
                            {D.BTN_ADD_ASSIGNMENT}
                        </button>
                    </div>
                )}
                {fieldTouched && formData.selectedProducts.length === 0 && (
                    <p className="field-error"><span className="field-error__icon">i</span>{validationProduct}</p>
                )}
            </div>
            )}
            {hideProductDropdown && formData.selectedCustomer && (isLoggedInAsUserManager || formData.selectedRole) && (
            <div className="um-add-row">
                <button
                    type="button"
                    className="btn btn-add"
                    onClick={handleAddAssignment}
                    disabled={fieldsDisabled || !formData.selectedRole}
                    aria-label={D.ARIA_ADD_ASSIGNMENT}
                    title={D.TOOLTIP_ADD_ASSIGNMENT}
                >
                    {D.BTN_ADD_ASSIGNMENT}
                </button>
            </div>
            )}
            {assignments.length > 0 && (
                <div className="um-assignments-table">
                    {assignments.map((a, i) => (
                        <div key={`${a.customerId}-${a.roleId}-${a.productIds.join(',')}-${i}`} className="um-assignments-table__row">
                            <div className="um-assignments-table__cell">
                                {getCustomerLabel(a.customerId, a.customerLabel, isLoggedInAsUserManager)}
                            </div>
                            <div className="um-assignments-table__cell">{a.roleLabel}</div>
                            <div className="um-assignments-table__cell">{a.productLabels.join(', ') || D.LABEL_EMPTY_VALUE}</div>
                            <button
                                type="button"
                                className="um-assignments-table__remove"
                                onClick={() => removeAssignment(i)}
                                aria-label={D.ARIA_REMOVE_ASSIGNMENT}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {assignmentError && (
                <p className="field-error"><span className="field-error__icon">i</span>{assignmentError}</p>
            )}
            {submitTouched && assignments.length === 0 && !isAdminLockedMode && !hasPendingAssignment && (
                <p className="field-error"><span className="field-error__icon">i</span>{validationNoAssignments}</p>
            )}
            <div className="button-group">
                <button className="btn btn-cancel" onClick={onCancel} disabled={submitting}>{cancelLabel}</button>
                <button className="btn btn-add" onClick={handleSubmit} disabled={submitting}>
                    {getSubmitLabel(submitting, submitLabel, isEditMode ? D.BTN_SAVE_CHANGES : D.BTN_ADD_USER)}
                </button>
            </div>
        </div>
    )
}
export default AssignUserTab

